import { Buffer } from 'buffer';
import { Connection, VersionedTransaction, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';

const JUPITER_API = 'https://quote-api.jup.ag/v6';
const SOL_MINT    = 'So11111111111111111111111111111111111111112';
function getHeliusRpcUrl() {
  const key = localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
  return key ? `https://mainnet.helius-rpc.com/?api-key=${key}` : 'https://api.mainnet-beta.solana.com';
}

const toLamports = (sol) => Math.floor(sol * LAMPORTS_PER_SOL);

let _conn = null;
function getConnection() {
  const url = getHeliusRpcUrl();
  if (!_conn || _conn.rpcEndpoint !== url) {
    _conn = new Connection(url, 'confirmed');
  }
  return _conn;
}

// Exponential backoff avec jitter pour les erreurs 429/5xx
async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const r = await fetch(url, options);
      if (r.status === 429 || r.status >= 500) {
        const wait = Math.pow(2, i) * 800 + Math.random() * 400;
        await new Promise(res => setTimeout(res, wait));
        continue;
      }
      return r;
    } catch (e) {
      if (i === maxRetries - 1) throw e;
      await new Promise(res => setTimeout(res, Math.pow(2, i) * 600));
    }
  }
  throw new Error('Jupiter API inaccessible après ' + maxRetries + ' tentatives');
}

async function getTokenDecimals(mint) {
  try {
    const conn = getConnection();
    const info = await conn.getParsedAccountInfo(new PublicKey(mint));
    return info?.value?.data?.parsed?.info?.decimals ?? 6;
  } catch { return 6; }
}

export async function getQuote({ inputMint, outputMint, amount, slippageBps = 100 }) {
  const url = `${JUPITER_API}/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amount}&slippageBps=${slippageBps}&onlyDirectRoutes=false`;
  const r = await fetchWithRetry(url);
  if (!r.ok) {
    const err = await r.text();
    throw new Error(`Jupiter quote failed: ${r.status} — ${err}`);
  }
  return r.json();
}

export async function executeSwap({ keypair, quote, jitoTip = 'low' }) {
  const connection = getConnection();

  // Fees de priorité augmentés pour meilleur taux de succès
  const jitoTipMap = { low: 5000, medium: 25000, high: 100000 };
  const tipLamports = jitoTipMap[jitoTip] || 5000;

  const swapBody = {
    quoteResponse:             quote,
    userPublicKey:             keypair.publicKey.toBase58(),
    wrapAndUnwrapSol:          true,
    prioritizationFeeLamports: tipLamports,
    dynamicComputeUnitLimit:   true,
  };

  const swapRes = await fetchWithRetry(`${JUPITER_API}/swap`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(swapBody),
  });

  if (!swapRes.ok) {
    const err = await swapRes.text();
    throw new Error(`Jupiter swap failed: ${swapRes.status} — ${err}`);
  }

  const { swapTransaction } = await swapRes.json();

  const txBuf   = Buffer.from(swapTransaction, 'base64');
  const tx      = VersionedTransaction.deserialize(txBuf);
  const blockhash = tx.message.recentBlockhash;
  const { lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  tx.sign([keypair]);
  const rawTx = tx.serialize();

  const sig = await connection.sendRawTransaction(rawTx, {
    skipPreflight:        true,
    maxRetries:           3,
    preflightCommitment:  'confirmed',
  });

  // Confirmation bloquante avec la nouvelle API (blockhash strategy)
  const confirmResult = await Promise.race([
    connection.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed'),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout confirmation TX (45s)')), 45000)
    ),
  ]);

  if (confirmResult?.value?.err) {
    throw new Error(`TX rejetée on-chain: ${JSON.stringify(confirmResult.value.err)}`);
  }

  return { signature: sig };
}

export async function buyToken({ keypair, tokenMint, amountSol, slippageBps = 100, jitoTip = 'low' }) {
  const amountLamports = toLamports(amountSol);
  const quote = await getQuote({
    inputMint: SOL_MINT, outputMint: tokenMint,
    amount: amountLamports,
    slippageBps: Math.max(slippageBps, 100),
  });
  return executeSwap({ keypair, quote, jitoTip });
}

export async function sellToken({ keypair, tokenMint, amountTokens, slippageBps = 100, jitoTip = 'low' }) {
  const decimals  = await getTokenDecimals(tokenMint);
  const amountRaw = Math.floor(amountTokens * Math.pow(10, decimals));
  if (amountRaw <= 0) throw new Error('Montant de tokens trop faible');
  const quote = await getQuote({
    inputMint: tokenMint, outputMint: SOL_MINT,
    amount: amountRaw,
    slippageBps: Math.max(slippageBps, 100),
  });
  return executeSwap({ keypair, quote, jitoTip });
}

export async function sellTokenPercent({ keypair, tokenMint, percentToSell, slippageBps = 100, jitoTip = 'low' }) {
  const connection = getConnection();
  const accounts   = await connection.getTokenAccountsByOwner(
    keypair.publicKey,
    { mint: new PublicKey(tokenMint) },
    { encoding: 'jsonParsed' }
  );
  if (!accounts.value.length) throw new Error('Token non trouvé dans le wallet');

  const tokenInfo    = accounts.value[0].account?.data?.parsed?.info;
  const balance      = parseFloat(tokenInfo?.tokenAmount?.uiAmount || 0);
  const decimals     = tokenInfo?.tokenAmount?.decimals ?? 6;
  if (balance <= 0) throw new Error('Solde token insuffisant');

  const amountToSell = balance * (percentToSell / 100);
  const amountRaw    = Math.floor(amountToSell * Math.pow(10, decimals));
  if (amountRaw <= 0) throw new Error('Montant calculé trop faible');

  const quote = await getQuote({
    inputMint: tokenMint, outputMint: SOL_MINT,
    amount: amountRaw,
    slippageBps: Math.max(slippageBps, 100),
  });
  return executeSwap({ keypair, quote, jitoTip });
}
